import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout, ExerciseMedia, isExternalMediaLink } from "./ClientHome";
import { Search, ExternalLink, PlayCircle } from "lucide-react";

export default function ExerciseLibrary() {
  const { t } = useLang();
  const [exercises, setExercises] = useState(null);
  const [muscle, setMuscle] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/exercises").then((r) => setExercises(r.data)).catch(() => setExercises([]));
  }, []);

  const groups = useMemo(() => {
    const set = new Set();
    (exercises || []).forEach((e) => e.muscle_group && set.add(e.muscle_group));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [exercises]);

  const filtered = useMemo(() => (exercises || []).filter((e) =>
    (!muscle || e.muscle_group === muscle) &&
    (!q || e.name.toLowerCase().includes(q.toLowerCase()))
  ), [exercises, muscle, q]);

  return (
    <ClientLayout>
      <div className="max-w-3xl mx-auto w-full" data-testid="library-page">
        <p className="kc-eyebrow mb-2">{t("lib.sub")}</p>
        <h1 className="text-3xl font-black uppercase tracking-tight mb-5">{t("lib.title")}</h1>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input className="kc-input !pl-10" placeholder={t("lib.search")} value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="library-search" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-5" data-testid="library-filters">
          <button onClick={() => setMuscle("")} data-testid="library-filter-all"
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-colors whitespace-nowrap ${!muscle ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
            {t("lib.all")}
          </button>
          {groups.map((g) => (
            <button key={g} onClick={() => setMuscle(g)} data-testid={`library-filter-${g.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-colors whitespace-nowrap ${muscle === g ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
              {g}
            </button>
          ))}
        </div>

        {!exercises ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground" data-testid="library-empty">{t("lib.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((e) => (
              <div key={e._id} className="kc-card overflow-hidden !p-0" data-testid={`library-exercise-${e._id}`}>
                {e.video_url && !isExternalMediaLink(e.video_url) ? (
                  <ExerciseMedia url={e.video_url} lazy className="w-full aspect-video object-cover bg-secondary/40" />
                ) : e.video_url ? (
                  <a href={e.video_url} target="_blank" rel="noreferrer"
                    className="w-full aspect-video bg-secondary/40 flex items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                    <ExternalLink className="w-5 h-5" /><span className="text-sm font-semibold">{t("lib.watchVideo")}</span>
                  </a>
                ) : (
                  <div className="w-full aspect-video bg-secondary/40 flex items-center justify-center">
                    <PlayCircle className="w-8 h-8 text-muted-foreground/30" />
                  </div>
                )}
                <div className="p-4">
                  <p className="font-bold text-sm leading-tight">{e.name}</p>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {e.muscle_group && <span className="kc-badge bg-primary/10 text-primary border border-primary/30">{e.muscle_group}</span>}
                    {e.equipment && <span className="kc-badge bg-secondary text-muted-foreground border border-border">{e.equipment}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
